import { FilePicker } from '@capawesome/capacitor-file-picker';
import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    Output,
    Signal,
    signal,
    ViewChild,
    WritableSignal
} from '@angular/core';

import {
    IonButton,
    IonModal,
    IonRippleEffect,
    IonSpinner,
    isPlatform,
    ModalController
} from '@ionic/angular/standalone';

import { Camera, CameraResultType, Photo } from '@capacitor/camera';

import { DateTime } from 'luxon';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';

import { bytesToMegabytes, fileToBase64, getExpiryYearsForDocument, prependBase64String } from '../../../helpers';
import { MobileAppService } from '../../../services/mobile-app/mobile-app.service';
import { ToastService } from '../../../services/toast/toast.service';
import { DOCUMENT_TYPE_OPTIONS, MONTH_OPTIONS } from '../../../shared';
import { DocumentObjectType, DocumentTypes } from '../../../types';
import { ActionSheetComponent } from '../../action-sheet/action-sheet.component';
import { ButtonComponent } from '../../buttons/button/button.component';
import { FileItemComponent } from '../../file-item/file-item.component';
import { SvgIconComponent } from '../../icons/svg-icon/svg-icon.component';
import { SelectComponent } from '../select/select.component';

@Component({
    selector: 'lib-file-selector',
    imports: [
        CommonModule,
        ButtonComponent,
        SvgIconComponent,
        FileItemComponent,
        IonModal,
        SelectComponent,
        IonButton,
        IonRippleEffect,
        ImageCropperComponent,
        ActionSheetComponent,
        IonSpinner
    ],
    templateUrl: './file-selector.component.html',
    styleUrl: './file-selector.component.css'
})
export class FileSelectorComponent {
    /* eslint-disable */
    toastService = inject(ToastService);
    modalCtrl = inject(ModalController);
    mobileAppService = inject(MobileAppService);

    @Input() files: DocumentObjectType[] | null = [];
    @Input() documentType: DocumentTypes | string = '';

    @Input() hideButton: boolean = false;
    @Input() hideContent: boolean = false;

    @Input() showDocumentTypeSelector: boolean = false;

    @Input() label: string = '';
    @Input() buttonType: 'full' | 'text' = 'full';
    @Input() buttonLabel: string = 'Upload';
    @Input() additionalButtonLabel: string = 'Add one more document';
    @Input() modalTitle: string = '';

    @Input() showAsModal: boolean = true;
    @Input() acceptFileFormats: string[] = ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.doc', '.docx'];
    @Input() maxFiles: number = 2;
    @Input() maxFileSizeMb: number = 10;
    @Input() displayMaxToast: boolean = true;
    @Input() showExpirationInputs: boolean = true;
    @Input() autoCropImage: boolean = true;

    @Input() mobileImagesOnly: boolean = false;

    @Output() onFileSelect = new EventEmitter<DocumentObjectType[]>();
    @Output() onCancel = new EventEmitter();
    @Output() onUpload = new EventEmitter();

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    @ViewChild('documentModal') documentModal!: IonModal;
    @ViewChild('imageCropper') imageCropper!: ImageCropperComponent;
    @ViewChild('mobileActionSheet') mobileActionSheet!: ActionSheetComponent;

    acceptsImages: Signal<boolean> = computed(() =>
        this.acceptFileFormats ? this.isSelectedFileAnImageByFormat(this.acceptFileFormats) : true
    );

    acceptsDocuments: Signal<boolean> = computed(() =>
        this.acceptFileFormats ? this.isSelectedFileADocumentByFormat(this.acceptFileFormats) : true
    );

    mobileActionSheetButtons: Signal<Array<{ text: string; role: string }>> = computed(() => {
        const array = [];
        if (this.acceptsImages()) {
            array.push({
                text: 'Take a photo or select from gallery',
                role: 'photo'
            });
        }

        if (this.acceptsDocuments()) {
            array.push({
                text: 'Select from files',
                role: 'files'
            });
        }

        return array;
    });

    isLoadingDocument: WritableSignal<boolean> = signal(false);

    modalFileName: string = '';
    modalFileSize: number | null = null;
    modalDocumentType: DocumentTypes | string = '';
    modalSelectedDocumentType: string = '';
    modalBase64: string | null = null;
    modalMonth: string = '';
    modalYear: string = '';

    base64Image: WritableSignal<string | null> = signal(null);
    modalFileIndex: WritableSignal<number | null | undefined> = signal(null);

    generatedYearOptions = getExpiryYearsForDocument();
    yearOptions: Array<{ label: string; value: string }> = this.generatedYearOptions;
    readonly documentTypeOptions = DOCUMENT_TYPE_OPTIONS;

    monthOptions = MONTH_OPTIONS;

    currentDate = DateTime.now();

    clearFiles() {
        this.files = [];
        this.clearModalData();
    }

    updateValidMonthsByYear(year: string) {
        const selectedYear = parseInt(year);
        const currentYear = this.currentDate.year;

        if (selectedYear === currentYear) {
            const currentMonth = this.currentDate.month;
            const slicedMonths = MONTH_OPTIONS.slice(currentMonth - 1, MONTH_OPTIONS.length + 1);
            this.monthOptions = slicedMonths;

            if (this.modalMonth) {
                const isSelectedMonthInOption = slicedMonths.find(month => month.value === this.modalMonth);
                if (!isSelectedMonthInOption) {
                    this.modalMonth = '';
                }
            }
        } else {
            this.monthOptions = MONTH_OPTIONS;
        }
    }

    updateValidYearsByMonth(month: string) {
        if (month && (!this.modalYear || this.modalYear === this.currentDate.year.toString())) {
            const selectedMonthDT = DateTime.fromISO(`${this.currentDate.year}-${month}-01`);
            const currentDT = DateTime.fromISO(
                `${this.currentDate.year}-${this.currentDate.month.toString().padStart(2, '0')}-01`
            );

            if (selectedMonthDT < currentDT) {
                this.yearOptions = this.generatedYearOptions.slice(1, this.generatedYearOptions.length + 1);
                this.modalYear = '';
            }
        } else {
            this.yearOptions = this.generatedYearOptions;
        }
    }

    onSelectChange(event: string, type: 'month' | 'year') {
        if (type === 'month') {
            this.modalMonth = event;
            this.updateValidYearsByMonth(event);
        } else if (type === 'year') {
            this.modalYear = event;
            this.updateValidMonthsByYear(event);
        }
    }

    onDocumentTypeSelect(event: string) {
        this.modalDocumentType = event;
    }

    onImageCrop(event: ImageCroppedEvent) {
        console.log('image cropped');
        this.isLoadingDocument.set(false);

        if (event.base64) {
            this.modalBase64 = event.base64;
        }
    }

    showModal(file?: DocumentObjectType, index?: number | null) {
        this.modalFileIndex.set(index);

        const hasReachedMaxFiles = this.files && this.files.length >= this.maxFiles && !file;

        if (hasReachedMaxFiles && this.displayMaxToast) {
            this.toastService.errorToast({ message: `Maximum of ${this.maxFiles} files allowed` });
            return;
        }

        this.modalFileName = file?.name || '';
        this.modalFileSize = file?.size || null;
        this.modalYear = file?.expiration_year || '';
        this.modalMonth = file?.expiration_month || '';
        this.modalBase64 = file?.base64 || null;
        this.modalDocumentType = file?.type || this.documentType;

        this.documentModal.present();
    }

    dismissModal() {
        this.clearModalData();
        this.documentModal.dismiss();
    }

    onActionSheetClick(event: { text: string; role?: string }) {
        this.mobileActionSheet.dismissActionSheet();
        this.documentModal.present();

        const role = event.role;
        if (role === 'photo') {
            this.getCameraPhotoOnMobile();
        } else if (role === 'files') {
            this.getDocumentFileOnMobile();
        }
    }

    onActionSheetDidDismiss(event: CustomEvent) {
        const role = event.detail.role;

        if (role === 'backdrop') {
            this.documentModal.present();
        }
    }

    async selectFile() {
        if (isPlatform('capacitor') && this.mobileActionSheetButtons().length) {
            if (this.mobileImagesOnly) {
                this.getCameraPhotoOnMobile();
                return;
            }

            this.documentModal.dismiss();
            this.mobileActionSheet.showActionSheet();
        } else {
            this.fileInput.nativeElement.click();
        }
    }

    async getDocumentFileOnMobile() {
        console.log('has permission');

        const hasFilePermission = await this.mobileAppService.hasFilesPermission();

        if (!hasFilePermission) {
            return;
        }

        const result = await FilePicker.pickFiles({ limit: 1, readData: true });
        const file = result.files[0];

        if (file) {
            this.isLoadingDocument.set(true);
            const base64File = prependBase64String(file.data || '', file.mimeType);
            const fileType = file.mimeType.split('/')[1] || file.mimeType;

            if (this.acceptsImages() && this.isSelectedFileAnImageByFormat([fileType])) {
                this.base64Image.set(base64File);
                this.imageCropper.crop('base64');
                this.modalFileName = file.name || `image.${file.mimeType.split('/')[1]}`;
            } else if (this.acceptsDocuments() && this.isSelectedFileADocumentByFormat([fileType])) {
                this.modalBase64 = base64File;
                this.modalFileName = file.name || `document.${file.mimeType.split('/')[1]}`;
                this.isLoadingDocument.set(false);
            } else {
                this.isLoadingDocument.set(false);

                this.toastService.errorToast({
                    message: `Please select a valid file format. Accepted files are: ${this.acceptFileFormats.join(', ')}`,
                    duration: 5000
                });
            }
        }
    }

    async getCameraPhotoOnMobile() {
        const hasCameraPermission = await this.mobileAppService.hasCameraPermission();

        if (!hasCameraPermission) {
            return;
        }

        const image = await Camera.getPhoto({
            quality: 80,
            resultType: CameraResultType.Base64,
            saveToGallery: true
        });

        this.modalFileName = `${this.documentType || image.exif.DateTime || 'image'}${image.format ? '.' + image.format : ''}`;
        this.handleSelectedPhotoFromCamera(image);
    }

    async handleSelectedPhotoFromCamera(event: Photo) {
        console.log(event);
        if (!event.base64String) return;

        const subtraction = event?.base64String && event?.base64String?.indexOf('==') > -1 ? 2 : 1;
        const estimatedSize = (event?.base64String?.length || 0) * (3 / 4) - subtraction;
        const estimatedSizeMb = Number(bytesToMegabytes(estimatedSize));

        if (!this.verifyFileSize(estimatedSizeMb)) return;

        if (this.isSelectedFileAnImageByFormat([event.format])) {
            this.base64Image.set(event.base64String);
            this.imageCropper.crop('base64');
        } else {
            this.modalBase64 = event?.base64String;
        }
    }

    isSelectedFileADocumentByFormat(format: string[]) {
        const cleanedFormats = format.map(f => f.replace('.', ''));
        return cleanedFormats.includes('pdf') || cleanedFormats.includes('doc') || cleanedFormats.includes('docx');
    }

    isSelectedFileAnImageByFormat(format: string[]) {
        const cleanedFormats = format.map(f => f.replace('.', ''));
        return (
            cleanedFormats.includes('jpeg') ||
            cleanedFormats.includes('png') ||
            cleanedFormats.includes('jpg') ||
            cleanedFormats.includes('image')
        );
    }

    async selectFileChangeOnInput(event: any) {
        const file = event.target.files[0];

        const calculatedMb = Math.round((file.size / (1024 * 1024)) * 100) / 100;

        if (!this.verifyFileSize(calculatedMb)) return;

        this.isLoadingDocument.set(true);

        const base64 = await fileToBase64(file);

        this.modalBase64 = base64;
        this.modalFileName = file.name;
        this.modalFileSize = file.size;

        if (file.type.includes('image') && this.autoCropImage) {
            this.base64Image.set(base64);
            this.imageCropper.crop('base64');
        } else {
            this.isLoadingDocument.set(false);
        }
    }

    onImageLoadFail() {
        this.toastService.errorToast({ message: 'Could not load image. Please select a different one.' });
        this.modalBase64 = null;
        this.modalFileName = '';
        this.modalFileSize = null;
        this.base64Image.set(null);
        this.isLoadingDocument.set(false);
    }

    verifyFileSize(sizeMb: number) {
        if (sizeMb && sizeMb > this.maxFileSizeMb) {
            this.toastService.errorToast({ message: `File size must be less than ${this.maxFileSizeMb} mb.` });
            return false;
        } else {
            return true;
        }
    }

    removeModalFile() {
        this.modalBase64 = null;
    }

    onFileRemove(index: number) {
        this.files?.splice(index, 1);
        this.onFileSelect.emit(this.files || undefined);
    }

    clearModalData() {
        this.modalYear = '';
        this.modalMonth = '';
        this.modalBase64 = '';
        this.modalDocumentType = '';
        this.base64Image.set(null);
        this.modalFileIndex.set(null);
        this.modalFileName = '';
        this.modalFileSize = null;
    }

    async addDocument() {
        if (this.showExpirationInputs && (!this.modalBase64 || !this.modalYear || !this.modalMonth)) {
            this.toastService.errorToast({ message: `All fields are required.` });
            return;
        } else if (!this.modalBase64) {
            this.toastService.errorToast({ message: `Select a document` });
            return;
        }

        const newFile: DocumentObjectType = {
            base64: this.modalBase64,
            expiration_month: this.modalMonth,
            expiration_year: this.modalYear,
            name: this.modalFileName,
            type: this.modalDocumentType || this.documentType,
            size: this.modalFileSize || undefined
        };

        const index = this.modalFileIndex();

        if (index !== null && index !== undefined) {
            this.files?.splice(index, 1, newFile);
        } else {
            this.files = [...(this.files || []), newFile];
        }

        this.onFileSelect.emit(this.files || []);

        this.documentModal.dismiss();

        this.clearModalData();
    }

    onCancelClick() {
        this.dismissModal();
        this.onCancel.emit();
    }

    onUploadButtonClick(documentType?: string) {
        if (documentType) {
            this.documentType = documentType || '';
        }
        this.showModal();
        this.onUpload.emit();
    }
}
